import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Plus } from "lucide-react";
import api from "@/lib/api";
import { TRACKER_LIST } from "@/lib/trackers";

function fmtDate(iso) {
  const d = new Date(iso + "T00:00:00");
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

function TrackerCard({ item, range, navigate }) {
  const { slug, type, title, icon: Icon } = item;
  const [data, setData] = useState({ series: [], total: 0, week_total: 0 });

  useEffect(() => {
    api.get(`/trackers/${type}?range=${range}`).then((r) => setData(r.data)).catch(() => {});
  }, [type, range]);

  const chartData = data.series.map((s) => ({ date: fmtDate(s.date), count: s.count }));

  return (
    <div className="bg-white rounded-2xl p-5 card-shadow" data-testid={`tracker-card-${type}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-sage flex items-center justify-center">
            <Icon size={16} className="text-brand" />
          </span>
          <span className="font-semibold text-ink text-sm">{title}</span>
        </div>
        <button
          data-testid={`tracker-log-${type}`}
          onClick={() => navigate(slug === "hot-flash-diary" ? "/hot-flash-diary" : slug === "weekly-sleep-quality-journal" ? "/sleep-journal" : `/printable/${slug}`)}
          className="flex items-center gap-1 text-xs font-semibold text-brand bg-sage px-3 py-1.5 rounded-full"
        >
          <Plus size={13} /> Log
        </button>
      </div>
      {chartData.length === 0 ? (
        <div className="h-[120px] flex items-center justify-center text-ink-muted text-sm text-center px-4">
          No entries yet, tap Log to add one.
        </div>
      ) : (
        <>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="font-serif text-2xl font-bold text-ink">{data.week_total}</span>
            <span className="text-ink-muted text-xs">this week · {data.total} in {range} days</span>
          </div>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={chartData} margin={{ top: 5, right: 5, left: -28, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5DFD5" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#88988E" }} tickLine={false} axisLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#88988E" }} tickLine={false} axisLine={false} />
              <Tooltip cursor={{ fill: "rgba(59,89,76,0.06)" }} />
              <Bar dataKey="count" fill="#3B594C" radius={[4, 4, 0, 0]} maxBarSize={26} />
            </BarChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
}

export default function TrackerTrends({ range }) {
  const navigate = useNavigate();
  return (
    <div data-testid="tracker-trends">
      <div className="bg-app-secondary border border-subtle rounded-2xl p-4 mt-4">
        <p className="text-xs text-ink-muted leading-relaxed">
          These are episodes you've logged in your trackers. They help you spot patterns over time, not a diagnosis or medical assessment.
        </p>
      </div>
      <div className="mt-4 space-y-4">
        {TRACKER_LIST.map((item) => (
          <TrackerCard key={item.type} item={item} range={range} navigate={navigate} />
        ))}
      </div>
    </div>
  );
}
